// ~/apps/themusicchurch-app/frontend/src/components/Layout/Footer.jsx

import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FiFacebook, FiInstagram, FiYoutube, FiMail } from "react-icons/fi";

const FooterWrapper = styled.footer`
  position: relative;
  background: url("/images/footer-background-001.png") center/cover;
  color: ${(props) => props.theme.colors.white};
  padding: 32px 0;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => props.theme.colors.darkBg};
    z-index: 1;
  }
`;

const FooterContainer = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1120px;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0 24px;
  @media (max-width: 600px) { padding: 0 20px; }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 32px 24px;
  align-items: start;
  margin-bottom: 32px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 520px) { grid-template-columns: minmax(0, 1fr); gap: 24px; }
`;

const FooterColumn = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
`;

const FooterTitle = styled.h4`
  color: ${(props) => props.theme.colors.white};
  margin-bottom: 12px;
  font-size: ${(props) => props.theme.fontSizes.lg};
`;

const FooterList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing.sm};
`;

const FooterLink = styled(Link)`
  color: ${(props) => props.theme.colors.gray[300]};
  transition: color ${(props) => props.theme.transitions.fast};

  &:hover {
    color: ${(props) => props.theme.colors.accent};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.lg};
  margin-top: ${(props) => props.theme.spacing.lg};
`;

const SocialLink = styled.a`
  color: ${(props) => props.theme.colors.gray[300]};
  font-size: ${(props) => props.theme.fontSizes.xl};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    color: ${(props) => props.theme.colors.accent};
    transform: translateY(-2px);
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: ${(props) => props.theme.spacing.lg};
  text-align: center;
  color: ${(props) => props.theme.colors.gray[400]};
  font-size: ${(props) => props.theme.fontSizes.sm};
`;

const FooterText = styled.p`
  color: ${(props) => props.theme.colors.gray[300]};
  margin-bottom: ${(props) => props.theme.spacing.md};
  line-height: 1.6;
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterWrapper>
      <FooterContainer>
        <FooterGrid>
          <FooterColumn>
            <FooterTitle>The Music Church</FooterTitle>
            <FooterText>
              A support group of imperfect people, here for one another.
            </FooterText>
            <FooterText style={{ fontWeight: "600", fontSize: "1.1rem" }}>
              Ministry Before Industry.
            </FooterText>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Ministries</FooterTitle>
            <FooterList>
              <li>
                <FooterLink to="/ministries/the-millennium">
                  The Millennium
                </FooterLink>
              </li>
              <li>
                <FooterLink to="/ministries/caught-up-club">
                  Caught-Up Club
                </FooterLink>
              </li>
              <li>
                <FooterLink to="/ministries/monday-musicians">
                  Monday Musicians
                </FooterLink>
              </li>
              <li>
                <FooterLink to="/ministries/care-music-tours">
                  Care Music Tours
                </FooterLink>
              </li>
              <li>
                <FooterLink to="/ministries/ministry-before-industry">
                  Ministry Before Industry
                </FooterLink>
                <ul style={{ listStyle: "none", paddingLeft: "16px", marginTop: "8px" }}>
                  <li><FooterLink to="/books">Books</FooterLink></li>
                </ul>
              </li>
              <li>
                <FooterLink to="/ministries/worship-academy">
                  Worship Academy
                </FooterLink>
              </li>
              <li>
                <FooterLink to="/ministries/quiet-kindness">
                  Quiet Kindness
                </FooterLink>
              </li>
            </FooterList>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Quick Links</FooterTitle>
            <FooterList>
              <li>
                <FooterLink to="/about">About Us</FooterLink>
              </li>
              <li>
                <FooterLink to="/events">Events</FooterLink>
              </li>
              <li>
                <FooterLink to="/store">Store</FooterLink>
              </li>
              <li>
                <FooterLink to="/contact">Contact</FooterLink>
              </li>
            </FooterList>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Connect</FooterTitle>
            <FooterText>Stay connected with The Music Church</FooterText>
            <SocialLinks>
              <SocialLink href="#" aria-label="Facebook">
                <FiFacebook />
              </SocialLink>
              <SocialLink href="#" aria-label="Instagram">
                <FiInstagram />
              </SocialLink>
              <SocialLink href="#" aria-label="YouTube">
                <FiYoutube />
              </SocialLink>
              <SocialLink
                href="mailto:themusicchurch@gmail.com"
                aria-label="Email"
              >
                <FiMail />
              </SocialLink>
            </SocialLinks>
            <FooterText style={{ marginTop: "1.5rem", fontSize: "0.875rem" }}>
              Email: themusicchurch@gmail.com
            </FooterText>
          </FooterColumn>
        </FooterGrid>

        <FooterBottom>
          <p>&copy; {currentYear} The Music Church. All rights reserved.</p>
          <p style={{ marginTop: "0.5rem", opacity: 0.7 }}>
            "Do not neglect to show hospitality to strangers, for by this some
            have entertained angels without knowing it." - Hebrews 13:2
          </p>
        </FooterBottom>
      </FooterContainer>
    </FooterWrapper>
  );
};

export default Footer;
